import moment from 'moment-timezone'

import {MINUTES_INTERVAL} from './constants'
import {TimeSlot} from './types'

// Time formats and constants matching mobile app
export const timeFormat = 'HH:mm'
export const dateFormat = 'YYYY-MM-DD'
export const dateTimeFormat = `${dateFormat} HH:mm:ss`
export const dateTimeFormatShort = `${dateFormat} ${timeFormat}`

/**
 * Normalize time to business hours and round to nearest interval
 */
export const normalizeTime = (
  time: moment.Moment,
  minTime?: string,
  maxTime?: string
): moment.Moment => {
  if (!minTime || !maxTime) return time

  const minDateTime = moment(time).startOf('day').add(moment.duration(minTime))
  const maxDateTime = moment(time).startOf('day').add(moment.duration(maxTime))

  if (time.isBefore(minDateTime)) return minDateTime
  if (time.isAfter(maxDateTime)) return maxDateTime

  // Round to nearest 15-minute interval
  const minutes = time.minutes()
  const roundedMinutes = Math.round(minutes / MINUTES_INTERVAL) * MINUTES_INTERVAL
  return time.clone().minutes(roundedMinutes).seconds(0)
}

/**
 * Check if time is in morning (before 12:00)
 */
export const isMorning = (time: string): boolean => {
  const hour = moment(time, timeFormat).hours()
  return hour < 12
}

/**
 * Check if time is in afternoon (12:00 - 17:59)
 */
export const isAfternoon = (time: string): boolean => {
  const hour = moment(time, timeFormat).hours()
  return hour >= 12 && hour < 18
}

/**
 * Check if time is in evening (18:00 and later)
 */
export const isEvening = (time: string): boolean => {
  const hour = moment(time, timeFormat).hours()
  return hour >= 18
}

/**
 * Split time slots by time of day categories
 */
export const splitTimeSlotsByTimeOfTheDay = (slots: TimeSlot[]) => {
  const morning: TimeSlot[] = []
  const afternoon: TimeSlot[] = []
  const evening: TimeSlot[] = []

  slots.forEach((slot) => {
    if (isMorning(slot.slot)) {
      morning.push(slot)
    } else if (isAfternoon(slot.slot)) {
      afternoon.push(slot)
    } else if (isEvening(slot.slot)) {
      evening.push(slot)
    }
  })

  return {Morning: morning, Afternoon: afternoon, Evening: evening}
}

/**
 * Format duration in different units
 * TODO: Move to shared utils package when available
 */
export const formatDuration = (value: number, unit: 'minutes' | 'hours' | 'seconds'): string => {
  switch (unit) {
    case 'minutes':
      return `${value}m`
    case 'hours':
      return `${value}h`
    case 'seconds':
      return `${value}s`
    default:
      return `${value}`
  }
}

/**
 * Convert minutes to HH:MM:SS time format
 * @param minutes The number of minutes to convert
 * @returns Time string in HH:MM:SS format
 * @example formatMinutesToTimeString(77) // returns "01:17:00"
 */
export const formatMinutesToTimeString = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:00`
}

export const convertToLocaleTime = (time: string, timeZone: string, timeFormat: string) => {
  return moment.tz(time, timeFormat, timeZone).local().format(timeFormat)
}
